/**
 * userDirectory.js
 * ─────────────────
 * In-memory directory of users available for task assignment and approval.
 * In production this comes from the tenant's user/IAM table; here it's seeded
 * from the demo logins plus a small cast of named team members per tenant
 * so auto-assignment has real candidates to pick from.
 *
 * Structure
 * ─────────
 *   id         , stable identifier
 *   name       , display name shown in UI
 *   email      , email (used as the assignment "address")
 *   role       , one of ROLES from rbac.js
 *   department , for department-scoped routing
 *   active     , false for users who exist but should not receive new assignments
 *   tenantId   , which tenant this user belongs to (null = available to all in demo)
 */

import { ROLES } from './rbac';

// Demo user directory. Realistic enough that auto-assignment routes feel
// natural in the demo, abstract enough that no real person's data is used.
const DIRECTORY = [
  // ─── ClearEqual demo logins ───────────────────────────────────────────
  { id: 'admin@clearequal.com',    name: 'Admin User',     email: 'admin@clearequal.com',    role: ROLES.ADMIN,    department: 'HR',                tenantId: null },
  { id: 'manager@clearequal.com',  name: 'Manager User',   email: 'manager@clearequal.com',  role: ROLES.MANAGER,  department: 'Engineering',       tenantId: null },
  { id: 'approver@clearequal.com', name: 'Approver User',  email: 'approver@clearequal.com', role: ROLES.APPROVER, department: 'HR',                tenantId: null },
  { id: 'auditor@clearequal.com',  name: 'Auditor User',   email: 'auditor@clearequal.com',  role: ROLES.AUDITOR,  department: null,                tenantId: null },
  { id: 'employee@clearequal.com', name: 'Employee User',  email: 'employee@clearequal.com', role: ROLES.EMPLOYEE, department: 'Engineering',       tenantId: null },

  // ─── Demo HR / Approver bench (named, used for assignment dropdowns) ──
  { id: 'sarah.chen',         name: 'Sarah Chen',         email: 'sarah.chen@clearequal.demo',         role: ROLES.ADMIN,    department: 'HR',                tenantId: null },
  { id: 'michael.rodriguez',  name: 'Michael Rodriguez',  email: 'michael.rodriguez@clearequal.demo',  role: ROLES.ADMIN,    department: 'Compensation',      tenantId: null },
  { id: 'emma.thompson',      name: 'Emma Thompson',      email: 'emma.thompson@clearequal.demo',      role: ROLES.ADMIN,    department: 'HR',                tenantId: null },
  { id: 'lisa.anderson',      name: 'Lisa Anderson',      email: 'lisa.anderson@clearequal.demo',      role: ROLES.APPROVER, department: 'HR',                tenantId: null },
  { id: 'david.kim',          name: 'David Kim',          email: 'david.kim@clearequal.demo',          role: ROLES.APPROVER, department: 'Legal',             tenantId: null },
  { id: 'priya.patel',        name: 'Priya Patel',        email: 'priya.patel@clearequal.demo',        role: ROLES.MANAGER,  department: 'Engineering',       tenantId: null },
  { id: 'jorge.alvarez',      name: 'Jorge Álvarez',      email: 'jorge.alvarez@clearequal.demo',      role: ROLES.MANAGER,  department: 'Sales',             tenantId: null },
  { id: 'sophie.dubois',      name: 'Sophie Dubois',      email: 'sophie.dubois@clearequal.demo',      role: ROLES.MANAGER,  department: 'Marketing',         tenantId: null },
  { id: 'thomas.weber',       name: 'Thomas Weber',       email: 'thomas.weber@clearequal.demo',       role: ROLES.MANAGER,  department: 'Operations',        tenantId: null },
  { id: 'anna.lindqvist',     name: 'Anna Lindqvist',     email: 'anna.lindqvist@clearequal.demo',     role: ROLES.MANAGER,  department: 'Finance',           tenantId: null },
];

/**
 * Returns all users (optionally filtered by tenant). The demo directory is
 * shared across tenants for simplicity; production would scope by tenantId.
 */
export const listUsers = (tenantId = null) => {
  if (!tenantId) return DIRECTORY;
  return DIRECTORY.filter((u) => u.tenantId === null || u.tenantId === tenantId);
};

/**
 * Convenience: list users with a specific role.
 */
export const listUsersByRole = (role, tenantId = null) => {
  return listUsers(tenantId).filter((u) => u.role === role);
};

/**
 * Look up a single user by id (typically the email).
 */
export const getUser = (id, tenantId = null) => {
  return listUsers(tenantId).find((u) => u.id === id) || null;
};

/**
 * Returns user options formatted for <Select> components.
 * If `roleFilter` is supplied, only users with that role are returned.
 */
export const getUserOptions = (roleFilter = null, tenantId = null) => {
  const users = roleFilter
    ? listUsersByRole(roleFilter, tenantId)
    : listUsers(tenantId);
  return users
    .filter((u) => u.active !== false)
    .map((u) => ({
      value: u.id,
      label: `${u.name}${u.department ? ` (${u.department})` : ''}`,
    }));
};
